import { c as ApiErrorDetector, g as globalCacheService, C as CacheTTL, i as isTokenExpired } from './index--OssIds0.js';
import { FetchRequest, JsonRpcProvider } from '@explorins/web3-ts/ethers';

/**
 * Platform-Agnostic Web3 Chain API Client
 *
 * Handles blockchain chain operations using the PERS backend.
 * Uses @explorins/web3-ts types for perfect framework compatibility.
 */
class Web3ChainApi {
    constructor(apiClient) {
        this.apiClient = apiClient;
        this.basePath = '/chains';
    }
    // ==========================================
    // PUBLIC OPERATIONS
    // ==========================================
    /**
     * PUBLIC: Get chain data by chain ID
     * Returns ChainDataDTO from the API (rpcUrl may be optional)
     */
    async getChainData(chainId) {
        try {
            const response = await this.apiClient.get(`${this.basePath}/${chainId}`);
            if (!response) {
                throw new Error(`No chain data received for chainId: ${chainId}`);
            }
            return response;
        }
        catch (error) {
            console.error('[Web3ChainApi] Failed to get chain data:', {
                chainId,
                error: error instanceof Error ? error.message : String(error)
            });
            throw error;
        }
    }
}

/**
 * Create Web3 provider from chain data
 * Synchronous function for immediate provider creation
 */
const getWeb3ProviderFromChainData = (chainData, timeout = 15000, customUserAgentName = '', tokenRefresher) => {
    let ethersProvider;
    if (chainData.authHeader) {
        // Authenticated provider for private chains
        const fetchRequest = new FetchRequest(chainData.rpcUrl);
        fetchRequest.timeout = timeout;
        // Handle auth header format
        const authValue = chainData.authHeader.startsWith('Bearer ')
            ? chainData.authHeader
            : `Bearer ${chainData.authHeader}`;
        fetchRequest.setHeader('Authorization', authValue);
        fetchRequest.setHeader('Content-Type', 'application/json');
        fetchRequest.setHeader('Accept', 'application/json');
        if (customUserAgentName) {
            fetchRequest.setHeader('User-Agent', customUserAgentName);
        }
        ethersProvider = new JsonRpcProvider(fetchRequest, chainData.chainId, {
            staticNetwork: true, // Prevent eth_chainId calls - we know the chain
            polling: false,
            batchMaxCount: 1, // Disable batching for private chains
        });
    }
    else {
        // Public provider for public chains
        ethersProvider = new JsonRpcProvider(chainData.rpcUrl, chainData.chainId, {
            staticNetwork: true, // Prevent eth_chainId calls - we know the chain
            polling: false,
        });
    }
    return ethersProvider;
};
/**
 * Async wrapper with retry logic for higher-level usage
 */
const getWeb3ProviderWithRetry = async (chainData, timeout = 15000, customUserAgentName = '', tokenRefresher, retryConfig = { maxAttempts: 3, baseDelay: 1000, maxDelay: 8000 }) => {
    let lastError = null;
    for (let attempt = 1; attempt <= retryConfig.maxAttempts; attempt++) {
        try {
            const provider = getWeb3ProviderFromChainData(chainData, timeout, customUserAgentName, tokenRefresher);
            await validateChainConnection(provider, chainData.authHeader ? 'private' : 'public');
            return provider;
        }
        catch (error) {
            lastError = error instanceof Error ? error : new Error(String(error));
            // Stop retrying on auth errors for authenticated chains
            if (ApiErrorDetector.isAuthError(lastError) && chainData.authHeader) {
                break;
            }
            if (attempt === retryConfig.maxAttempts)
                break;
            const delay = Math.min(retryConfig.baseDelay * Math.pow(2, attempt - 1), retryConfig.maxDelay);
            await sleep(delay);
        }
    }
    throw new Error(`Failed to create Web3 provider after ${retryConfig.maxAttempts} attempts. Last error: ${lastError?.message}`);
};
async function validateChainConnection(provider, chainType) {
    try {
        // Lightweight test using eth_chainId (works for both public and private)
        const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error(`${chainType} chain validation timeout`)), 3000);
        });
        const chainIdPromise = provider.send('eth_chainId', []);
        await Promise.race([chainIdPromise, timeoutPromise]);
    }
    catch (error) {
        // Fallback: try net_version if chainId fails
        try {
            const timeoutPromise = new Promise((_, reject) => {
                setTimeout(() => reject(new Error(`${chainType} chain fallback validation timeout`)), 3000);
            });
            const versionPromise = provider.send('net_version', []);
            await Promise.race([versionPromise, timeoutPromise]);
        }
        catch (fallbackError) {
            throw new Error(`${chainType} chain validation failed: ${error instanceof Error ? error.message : String(error)}`);
        }
    }
}
function sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

class Web3ChainService {
    constructor(web3ChainApi) {
        this.web3ChainApi = web3ChainApi;
        this.cache = globalCacheService.createNamespace('web3-chain');
    }
    async getEthersProviderByChainId(chainId) {
        const cacheKey = `provider:${chainId}`;
        // Check if we have a valid cached provider
        const cached = this.cache.get(cacheKey);
        if (cached && !this.checkIsTokenExpired(cached.chainData.authHeader)) {
            return cached.provider;
        }
        try {
            // Create new provider instance
            const { provider, chainData } = await this.createProviderInstance(chainId);
            // Cache the provider with chain data
            const cacheEntry = {
                provider,
                chainData,
                createdAt: Date.now(),
                chainId
            };
            this.cache.set(cacheKey, cacheEntry, CacheTTL.PROVIDER);
            return provider;
        }
        catch (error) {
            // ✅ AUTH ERROR RECOVERY: Clear cache and retry once
            if (ApiErrorDetector.isAuthError(error)) {
                this.cache.delete(cacheKey);
                // Retry once with fresh instance
                const { provider, chainData } = await this.createProviderInstance(chainId);
                const cacheEntry = {
                    provider,
                    chainData,
                    createdAt: Date.now(),
                    chainId
                };
                this.cache.set(cacheKey, cacheEntry, CacheTTL.PROVIDER);
                return provider;
            }
            throw error;
        }
    }
    async createProviderInstance(chainId) {
        const chainData = await this.getChainDataWithCache(chainId);
        if (!chainData) {
            throw new Error(`Chain data not found for chainId: ${chainId}`);
        }
        const provider = await getWeb3ProviderWithRetry(chainData);
        return { provider, chainData };
    }
    async getChainDataWithCache(chainId) {
        const cacheKey = `chainData:${chainId}`;
        // First check if we have it in the provider cache (includes token validation)
        const providerCached = this.cache.get(`provider:${chainId}`);
        if (providerCached && !this.checkIsTokenExpired(providerCached.chainData.authHeader)) {
            return providerCached.chainData;
        }
        // Otherwise use getOrSet for chainData only
        return this.cache.getOrSet(cacheKey, async () => {
            const chainData = await this.web3ChainApi.getChainData(chainId);
            if (!chainData) {
                throw new Error(`Chain data not found for chainId: ${chainId}`);
            }
            return chainData;
        }, CacheTTL.MEDIUM // 30 minutes for chain data
        );
    }
    checkIsTokenExpired(authHeader) {
        // NULL CHECK: Handle undefined case
        if (!authHeader) {
            return false; // No token means no expiration (public chains)
        }
        // FUNCTION CALL: Use imported function, not method
        return isTokenExpired(authHeader);
    }
    /**
     * Clear all cached providers and chain data
     * Useful for logout scenarios or when authentication context changes
     */
    clearCache() {
        this.cache.clear();
    }
    /**
     * Clear cache for a specific chain
     * Useful when a specific chain's authentication fails
     */
    clearCacheForChain(chainId) {
        this.cache.delete(`provider:${chainId}`);
        this.cache.delete(`chainData:${chainId}`);
    }
}

export { Web3ChainApi as W, Web3ChainService as a };
//# sourceMappingURL=web3-chain-service-BWRmwmmJ.js.map
